import { VIEW_GALLERY_CARD_META, VIEW_GALLERY_PAGE } from 'src/constants';
import viewStateVar from 'src/store/viewState';
import type { ViewNode } from 'src/types/store.types';

/**
 * All views response from GraphQL
 */
interface AllViewsData {
  allViews: {
    edges: ViewNode[];
  };
}

/**
 * View model reference (the found view node)
 */
type ViewModelRef = ViewNode | undefined;

/**
 * Generates and stores library view models from GraphQL data
 *
 * @param data - GraphQL response containing all views
 */
export const generateLibraryViewModels = (data: AllViewsData): void => {
  // Debug: log all views received
  console.log(
    'generateLibraryViewModels called with:',
    data.allViews.edges.map((e) => ({
      viewName: e.node.viewName,
      componentName: e.node.componentName,
    })),
  );

  const ViewMediaCardListView: ViewModelRef = data.allViews.edges.find(
    (view) => view.node.componentName === 'MediaCardListView',
  );
  const ViewFilterBarFirstDropdown: ViewModelRef = data.allViews.edges.find(
    (view) => view.node.componentName === 'FilterBarFirstDropdown',
  );
  const ViewMediaCardShowMetadata: ViewModelRef = data.allViews.edges.find(
    (view) => view.node.componentName === 'MediaCardShowMetadata',
  );
  const ViewMediaCardInfo: ViewModelRef = data.allViews.edges.find(
    (view) => view.node.componentName === 'MediaCardInfoButtonModal',
  );
  const ViewMediaCardHorizontalInfo: ViewModelRef = data.allViews.edges.find(
    (view) => view.node.componentName === 'MediaCardHorizontalInfo',
  );
  const ViewMediaCardHorizontalDetails: ViewModelRef = data.allViews.edges.find(
    (view) => view.node.componentName === 'MediaCardHorizontalDetails',
  );
  const ViewTechPanel: ViewModelRef = data.allViews.edges.find((view) => view.node.componentName === 'TechPanel');
  const ViewMediaCardInline: ViewModelRef = data.allViews.edges.find(
    (view) => view.node.componentName === 'MediaCardInline',
  );
  const ViewMediaCardMinimalInfo: ViewModelRef = data.allViews.edges.find(
    (view) => view.node.componentName === 'MediaCardMinimalInfo',
  );
  // Gallery views
  const ViewGalleryCardMeta: ViewModelRef = data.allViews.edges.find(
    (view) => view.node.componentName === VIEW_GALLERY_CARD_META && view.node.viewName === 'Gallery',
  );
  const ViewGalleryPage: ViewModelRef = data.allViews.edges.find(
    (view) => view.node.componentName === VIEW_GALLERY_PAGE && view.node.viewName === 'Gallery',
  );

  console.log('Gallery views found:', { ViewGalleryCardMeta, ViewGalleryPage });

  // Get current state to preserve existing values
  const currentViewModels = viewStateVar().viewModels;

  viewStateVar({
    ...viewStateVar(),
    viewModels: {
      ...currentViewModels,
      library: {
        ...currentViewModels.library,
        // Only update if we found the view (don't overwrite with undefined)
        ...(ViewMediaCardListView && { ViewMediaCardListView }),
        ...(ViewFilterBarFirstDropdown && { ViewFilterBarFirstDropdown }),
        ...(ViewMediaCardShowMetadata && { ViewMediaCardShowMetadata }),
        ...(ViewMediaCardInfo && { ViewMediaCardInfo }),
        ...(ViewMediaCardHorizontalInfo && { ViewMediaCardHorizontalInfo }),
        ...(ViewMediaCardHorizontalDetails && { ViewMediaCardHorizontalDetails }),
        ...(ViewTechPanel && { ViewTechPanel }),
        ...(ViewMediaCardInline && { ViewMediaCardInline }),
        ...(ViewMediaCardMinimalInfo && { ViewMediaCardMinimalInfo }),
      },
      gallery: {
        ...currentViewModels.gallery,
        // Only update if we found the view (don't overwrite with undefined)
        ...(ViewGalleryCardMeta && { ViewGalleryCardMeta }),
        ...(ViewGalleryPage && { ViewGalleryPage }),
      },
    },
  });
};

/**
 * Extracts only field labels from a view model in sequence order
 *
 * @param viewModel - The view model to extract labels from
 * @returns Array of field labels in sequence order
 */
export const getOnlyFieldLabels = (viewModel: ViewNode): string[] => {
  const edges = viewModel.node.viewfieldSet.edges.slice();
  edges.sort((a, b) => a.node.sequence - b.node.sequence);
  return edges.map((viewField) => viewField.node.fieldLabel);
};
